

import React, { useState } from 'react';
import { auth, db } from '../../firebase';
import { doc, setDoc } from 'firebase/firestore';
import type { Settings } from '../../types';

const GoogleIcon: React.FC<{className?:string}> = ({className}) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="24px" height="24px">
        <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
        <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
        <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.223,0-9.65-3.657-11.303-8H6.393c3.56,9.22,12.793,16,23.607,16z"/>
        <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.574l6.19,5.238C39.99,34.552,44,29.862,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
    </svg>
);

interface AdminLoginPageProps {
    settings: Settings;
    onLogin: (emailOrUsername: string, pass: string) => Promise<void>;
    onGoogleLogin: () => Promise<void>;
}

const AdminLoginPage: React.FC<AdminLoginPageProps> = ({ settings, onLogin, onGoogleLogin }) => {
    const [emailOrUsername, setEmailOrUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);

    const isLocalhost = window.location.hostname === 'localhost';

    const handleCreateSuperadmin = async () => {
        setLoading(true);
        setError('');
        setMessage('');
        try {
            const email = 'superadmin@arkitekns.com';
            const pass = 'SuperAdmin2026!';
            const username = 'superadmin';
            
            const userCredential = await auth.createUserWithEmailAndPassword(email, pass);
            const user = userCredential.user;
            
            if (user) {
                await setDoc(doc(db, 'users', user.uid), {
                    email,
                    role: 'developer',
                    username
                });
                setMessage('Superadmin account created successfully! You can now log in.');
            }
        } catch (err: any) {
            if (err.code === 'auth/email-already-in-use') {
                setError('Superadmin account already exists.');
            } else {
                setError(`Setup failed: ${err.message || err}`);
            }
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setMessage('');
        setLoading(true);
        try {
            await onLogin(emailOrUsername, password);
        } catch (err: any) {
            if (err.code === 'auth/invalid-credential' || err.code === 'auth/invalid-email' || err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.message === 'auth/user-not-found') {
                setError('Invalid credentials provided.');
            } else {
                setError('An unknown error occurred. Please try again.');
            }
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordReset = async () => {
        if (!emailOrUsername) {
            setError('Please enter your email address to reset password.');
            return;
        }
        setError('');
        setMessage('');
        try {
            await auth.sendPasswordResetEmail(emailOrUsername);
            setMessage('Password reset email sent! Check your inbox.');
        } catch (err: any) {
             if (err.code === 'auth/invalid-email') {
                setError('Please enter a valid email address.');
            } else {
                setError('Failed to send password reset email. Please try again.');
            }
        }
    }

    const handleGoogleLogin = async () => {
        setError('');
        setMessage('');
        setLoading(true);
        try {
            await onGoogleLogin();
        } catch (err: any) {
            if (err.message?.includes('not authorized')) {
                setError('Access Denied. Your email is not authorized to access this system.');
            } else {
                setError('Google sign-in failed. Please try again.');
            }
            console.error(err);
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-800">
            <div className="w-full max-w-md p-8 space-y-6 bg-gray-900 rounded-lg shadow-lg text-center">
                 <div className="flex justify-center items-center h-12">
                    {settings.logoUrl ? (
                        <img 
                            src={settings.logoUrl} 
                            alt={settings.companyName} 
                            style={{ width: `${settings.logoWidth || 120}px` }}
                            className="max-h-full object-contain"
                        />
                    ) : (
                         <h1 className="text-3xl font-bold text-white tracking-wider">
                            {settings.companyName}
                        </h1>
                    )}
                </div>
                
                <div className="text-center">
                    <p className="mt-2 text-gray-400">Admin Panel Access</p>
                </div>
                <form className="mt-8 space-y-6 text-left" onSubmit={handleSubmit}>
                    <div className="rounded-md shadow-sm space-y-4">
                        <div>
                            <label htmlFor="email-address" className="sr-only">Email or Username</label>
                            <input
                                id="email-address"
                                name="email"
                                type="text"
                                autoComplete="email username"
                                required
                                value={emailOrUsername}
                                onChange={(e) => setEmailOrUsername(e.target.value)}
                                className="appearance-none rounded-md relative block w-full px-3 py-3 border border-gray-700 bg-gray-800 text-white placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                                placeholder="Email or Username"
                            />
                        </div>
                        <div>
                            <label htmlFor="password" className="sr-only">Password</label>
                            <input
                                id="password"
                                name="password"
                                type="password"
                                autoComplete="current-password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="appearance-none rounded-md relative block w-full px-3 py-3 border border-gray-700 bg-gray-800 text-white placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                                placeholder="Password"
                            />
                        </div>
                    </div>

                    {error && <p className="text-red-500 text-sm text-center">{error}</p>}
                    {message && <p className="text-green-500 text-sm text-center">{message}</p>}

                    <div className="flex items-center justify-end text-sm">
                        <button type="button" onClick={handlePasswordReset} className="font-medium text-indigo-400 hover:text-indigo-300">
                            Forgot Password?
                        </button>
                    </div>

                    <div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-black bg-white hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white transition-colors disabled:bg-gray-400"
                        >
                            {loading ? 'Signing In...' : 'Sign In'}
                        </button>
                    </div>
                </form>

                <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-700"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-gray-900 text-gray-500">OR</span>
                    </div>
                </div>

                <div>
                    <button
                        type="button"
                        onClick={handleGoogleLogin}
                        disabled={loading}
                        className="group relative w-full flex justify-center items-center py-3 px-4 border border-gray-700 text-sm font-medium rounded-md text-white bg-gray-800 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-white transition-colors disabled:opacity-50"
                    >
                        <GoogleIcon className="mr-3"/>
                        Sign in with Google
                    </button>
                </div>

                {isLocalhost && (
                    <div className="mt-6 pt-6 border-t border-gray-700">
                        <button
                            type="button"
                            onClick={handleCreateSuperadmin}
                            disabled={loading}
                            className="w-full flex justify-center py-2 px-4 border border-transparent text-xs font-semibold rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none disabled:bg-gray-400"
                        >
                            Create Local Superadmin Account
                        </button>
                        <p className="mt-2 text-xs text-gray-500">Only visible on localhost. Will create an account with email <code>superadmin@arkitekns.com</code>, username <code>superadmin</code>, and password <code>SuperAdmin2026!</code></p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminLoginPage;